import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { ref, push, set, get, remove, update } from "firebase/database";

import { db, auth } from "./../firebase_config";
import { enqueueSnackbar } from "./notifications"; // Assuming you have this
import errorReporter from "../utils/errorReporting";

// Async thunk for adding a new account
export const addAccount = createAsyncThunk(
  "accounts/addAccount",
  async (accountData, { dispatch, rejectWithValue }) => {
    try {
      const newAccountRef = push(ref(db, "accounts"));
      await set(newAccountRef, {
        ...accountData,
        createdAt: Date.now(),
      });
      dispatch(
        enqueueSnackbar({
          message: "Account added successfully",
          options: { variant: "success" },
        }),
      );
      return { id: newAccountRef.key, ...accountData };
    } catch (error) {
      errorReporter.reportError(error, {
        component: 'accounts',
        action: 'add_account',
        severity: 'medium'
      });
      dispatch(
        enqueueSnackbar({
          message: "Failed to add account",
          options: { variant: "error" },
        }),
      );
      return rejectWithValue(error.message);
    }
  },
);

// Async thunk for editing an account
export const editAccount = createAsyncThunk(
  "accounts/editAccount",
  async (updates, { dispatch, rejectWithValue }) => {
    try {
      const { id } = updates;
      const accountRef = ref(db, `accounts/${id}`);
      await update(accountRef, updates);
      dispatch(
        enqueueSnackbar({
          message: "Account updated successfully",
          options: { variant: "success" },
        }),
      );
      return { id, ...updates };
    } catch (error) {
      errorReporter.reportError(error, {
        component: 'accounts',
        action: 'edit_account',
        account_id: updates.id,
        severity: 'medium'
      });
      dispatch(
        enqueueSnackbar({
          message: "Failed to update account",
          options: { variant: "error" },
        }),
      );
      return rejectWithValue(error.message);
    }
  },
);

// Async thunk for deleting an account
export const deleteAccount = createAsyncThunk(
  "accounts/deleteAccount",
  async (id, { dispatch, rejectWithValue }) => {
    try {
      const accountRef = ref(db, `accounts/${id}`);
      await remove(accountRef);
      dispatch(
        enqueueSnackbar({
          message: "Account deleted successfully",
          options: { variant: "success" },
        }),
      );
      return id;
    } catch (error) {
      dispatch(
        enqueueSnackbar({
          message: "Failed to delete account",
          options: { variant: "error" },
        }),
      );
      return rejectWithValue(error.message);
    }
  },
);

// Async thunk for querying accounts (unchanged)
export const queryAccounts = createAsyncThunk(
  "accounts/queryAccounts",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const accountsRef = ref(db, "accounts");
      const snapshot = await get(accountsRef);
      const accounts = [];
      snapshot.forEach((childSnapshot) => {
        accounts.push({ id: childSnapshot.key, ...childSnapshot.val() });
      });
      return accounts;
    } catch (error) {
      dispatch(
        enqueueSnackbar({
          message: "Failed to fetch accounts",
          options: { variant: "error" },
        }),
      );
      return rejectWithValue(error.message);
    }
  },
);

export const fetchUnlinkedAccounts = createAsyncThunk(
  "accounts/fetchUnlinked",
  async (_, { rejectWithValue }) => {
    try {
      // Residents can't read the accounts node directly (it holds every
      // neighbor's balance); the function returns only what the picker needs.
      const user = auth.currentUser;
      const idToken = await user.getIdToken();
      const response = await fetch(
        "https://us-central1-shelby-estates-hoa.cloudfunctions.net/listUnlinkedAccounts",
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
        },
      );
      const data = await response.json();
      if (response.ok && data.success) {
        return data.accounts;
      }
      return rejectWithValue(data.error || "Failed to load available accounts");
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

// Async thunk for unlinking an account from a user
export const unlinkAccount = createAsyncThunk(
  "accounts/unlinkAccount",
  async (accountId, { dispatch, rejectWithValue }) => {
    try {
      // Get the account to find linked users
      const accountSnapshot = await get(ref(db, `accounts/${accountId}`));
      const accountData = accountSnapshot.val();
      
      if (accountData && accountData.linkedAccounts) {
        // Remove user account links
        const linkedUsers = Object.keys(accountData.linkedAccounts);
        for (const userId of linkedUsers) {
          await remove(ref(db, `userAccounts/${userId}`));
        }
      }
      
      // Update account to unlinked status
      await update(ref(db, `accounts/${accountId}`), {
        linked: false,
        linkedAccounts: null
      });
      
      dispatch(
        enqueueSnackbar({
          message: "Account unlinked successfully",
          options: { variant: "success" },
        }),
      );
      
      return accountId;
    } catch (error) {
      dispatch(
        enqueueSnackbar({
          message: "Failed to unlink account",
          options: { variant: "error" },
        }),
      );
      return rejectWithValue(error.message);
    }
  },
);

// Helper function to fetch autopay status for a single user (with pre-loaded user data)
const fetchUserAutopayData = async (userId, userData, idToken) => {
  try {
    if (!userData || !userData.squareCustomerId) {
      return { userId, subscriptions: [], error: null };
    }
    
    // Call the fetchCustomerCards function to get subscription data
    const response = await fetch(
      "https://us-central1-shelby-estates-hoa.cloudfunctions.net/fetchCustomerCards",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          customerId: userData.squareCustomerId,
        }),
      }
    );
    
    if (response.ok) {
      const data = await response.json();
      if (data.success && data.subscriptions) {
        return { 
          userId, 
          customerId: userData.squareCustomerId,
          subscriptions: data.subscriptions,
          error: null 
        };
      }
    }
    
    return { userId, customerId: userData.squareCustomerId, subscriptions: [], error: 'API call failed' };
  } catch (error) {
    console.warn(`Failed to fetch autopay for user ${userId}:`, error);
    errorReporter.reportAutopayError(error, {
      action: 'fetch_user_autopay_data',
      user_id: userId,
      customer_id: userData?.squareCustomerId || 'unknown'
    });
    return { userId, customerId: userData.squareCustomerId || null, subscriptions: [], error: error.message };
  }
};

// Helper function to batch-fetch all user data from Firebase
const batchFetchUserData = async (userIds) => {
  try {
    console.log(`📦 Batch fetching ${userIds.length} users from Firebase...`);
    const startTime = Date.now();
    
    // Get all users data in one Firebase call
    const usersSnapshot = await get(ref(db, 'users'));
    const allUsersData = usersSnapshot.val() || {};
    
    // Extract only the users we need
    const relevantUsersData = {};
    userIds.forEach(userId => {
      relevantUsersData[userId] = allUsersData[userId] || null;
    });
    
    const endTime = Date.now();
    console.log(`✅ Firebase batch fetch completed in ${endTime - startTime}ms`);
    
    return relevantUsersData;
  } catch (error) {
    errorReporter.reportError(error, {
      component: 'accounts',
      action: 'batch_fetch_user_data',
      user_count: userIds.length
    });
    console.error('Error batch fetching user data:', error);
    throw error;
  }
};

// Async thunk for fetching autopay status for all accounts (BATCH + PARALLEL + CACHED)
export const fetchAccountsAutopayStatus = createAsyncThunk(
  "accounts/fetchAutopayStatus",
  async (forceRefresh = false, { getState, rejectWithValue }) => {
    try {
      const { accounts, autopayStatus, autopayLastFetched } = getState().accounts;
      
      // 💾 Cache check: Skip if data is less than 2 minutes old (unless force refresh)
      const CACHE_DURATION_MS = 2 * 60 * 1000; // 2 minutes
      const now = Date.now();
      const isCacheValid = autopayLastFetched && (now - autopayLastFetched < CACHE_DURATION_MS);
      
      if (isCacheValid && !forceRefresh && Object.keys(autopayStatus).length > 0) {
        console.log('💾 Using cached autopay data (< 2min old)');
        return autopayStatus; // Return existing cached data
      }
      
      if (forceRefresh) {
        console.log('🔄 Force refresh requested - bypassing cache');
      } else {
        console.log('🆕 Cache miss or expired - fetching fresh autopay data');
      }
      
      const autopayStatusMap = {};
      
      // Get current user's Firebase auth token
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("User not authenticated");
      }
      const idToken = await currentUser.getIdToken();
      
      // Build a mapping of accounts to their linked users
      const accountToUsers = {};
      const allUserIds = new Set();
      
      for (const account of accounts) {
        if (!account.linked || !account.linkedAccounts) {
          autopayStatusMap[account.id] = { status: 'not-linked', subscriptions: [], linkedUsers: [] };
          accountToUsers[account.id] = [];
        } else {
          const linkedUserIds = Object.keys(account.linkedAccounts);
          accountToUsers[account.id] = linkedUserIds;
          linkedUserIds.forEach(userId => allUserIds.add(userId));
        }
      }
      
      // ⚡ BATCH + PARALLEL EXECUTION: Get all user data, then fetch autopay in parallel
      console.log(`🚀 Batch + parallel fetching autopay data for ${allUserIds.size} users...`);
      const overallStartTime = Date.now();
      
      // Step 1: Batch fetch all user data from Firebase (1 call instead of N calls)
      const allUsersData = await batchFetchUserData(Array.from(allUserIds));
      
      // Step 2: Parallel fetch autopay data using the pre-loaded user data  
      const autopayStartTime = Date.now();
      console.log(`📦 Starting parallel autopay fetch for ${allUserIds.size} users...`);
      
      const userAutopayPromises = Array.from(allUserIds).map(userId => 
        fetchUserAutopayData(userId, allUsersData[userId], idToken)
      );
      
      const allUserResults = await Promise.all(userAutopayPromises);
      const autopayEndTime = Date.now();
      const overallEndTime = Date.now();
      
      console.log(`✅ Autopay parallel fetch completed in ${autopayEndTime - autopayStartTime}ms`);
      console.log(`🎯 Total batch+parallel fetch completed in ${overallEndTime - overallStartTime}ms`);
      
      // Report successful autopay fetch
      errorReporter.reportSuccess('fetch_autopay_status', 'autopay', {
        user_count: allUserIds.size,
        fetch_time_ms: overallEndTime - overallStartTime,
        account_count: accounts.length
      });
      
      // Create a lookup map of userId -> autopay data
      const userAutopayMap = {};
      allUserResults.forEach(result => {
        userAutopayMap[result.userId] = result;
      });
      
      // Now process results for each account
      for (const account of accounts) {
        if (autopayStatusMap[account.id]) {
          continue; // Already set as 'not-linked'
        }
        
        const linkedUserIds = accountToUsers[account.id];
        let hasActiveAutopay = false;
        let allSubscriptions = [];
        
        for (const userId of linkedUserIds) {
          const userResult = userAutopayMap[userId];
          if (userResult && userResult.subscriptions.length > 0) {
            allSubscriptions = [...allSubscriptions, ...userResult.subscriptions];
            
            // Check if any subscription is active or pending
            const activeSubscription = userResult.subscriptions.find(
              sub => sub.status === 'ACTIVE' || sub.status === 'PENDING'
            );
            if (activeSubscription) {
              hasActiveAutopay = true;
            }
          }
        }
        
        // Get linked user info for display
        const linkedUsers = linkedUserIds.map(userId => {
          const userData = allUsersData[userId];
          return userData ? {
            userId,
            firstName: userData.firstName || '',
            lastName: userData.lastName || '',
            email: userData.email || '',
            squareCustomerId: userData.squareCustomerId || null
          } : null;
        }).filter(Boolean);
        
        autopayStatusMap[account.id] = {
          status: hasActiveAutopay ? 'active' : 'inactive',
          subscriptions: allSubscriptions,
          linkedUsers: linkedUsers
        };
      }
      
      console.log('📊 Autopay status results:', autopayStatusMap);
      return autopayStatusMap;
    } catch (error) {
      const { accounts } = getState().accounts;
      const linkedAccounts = accounts.filter(account => account.linked);
      errorReporter.reportAutopayError(error, {
        action: 'fetch_all_autopay_status',
        account_count: accounts.length,
        linked_account_count: linkedAccounts.length,
        force_refresh: forceRefresh
      });
      console.error("Error fetching autopay status:", error);
      return rejectWithValue(error.message);
    }
  }
);

const accountsSlice = createSlice({
  name: "accounts",
  initialState: {
    accounts: [],
    unlinkedAccounts: [],
    autopayStatus: {},
    autopayLastFetched: null, // Timestamp for cache invalidation
    autopayLoading: false,
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Add Account
      .addCase(addAccount.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addAccount.fulfilled, (state, action) => {
        state.loading = false;
        state.accounts.push(action.payload);
      })
      .addCase(addAccount.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Edit Account
      .addCase(editAccount.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editAccount.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.accounts.findIndex(
          (account) => account.id === action.payload.id,
        );
        if (index !== -1) {
          state.accounts[index] = {
            ...state.accounts[index],
            ...action.payload,
          };
        }
      })
      .addCase(editAccount.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete Account
      .addCase(deleteAccount.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAccount.fulfilled, (state, action) => {
        state.loading = false;
        state.accounts = state.accounts.filter(
          (account) => account.id !== action.payload,
        );
      })
      .addCase(deleteAccount.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Query Accounts
      .addCase(queryAccounts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(queryAccounts.fulfilled, (state, action) => {
        state.loading = false;
        state.accounts = action.payload;
      })
      .addCase(queryAccounts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchUnlinkedAccounts.fulfilled, (state, action) => {
        state.unlinkedAccounts = action.payload;
        state.loading = false;
      })
      .addCase(unlinkAccount.fulfilled, (state, action) => {
        const accountId = action.payload;
        const account = state.accounts.find(acc => acc.id === accountId);
        if (account) {
          account.linked = false;
          account.linkedAccounts = null;
        }
        // Clear autopay status for unlinked account
        delete state.autopayStatus[accountId];
      })
      // Fetch Autopay Status
      .addCase(fetchAccountsAutopayStatus.pending, (state) => {
        state.autopayLoading = true;
      })
      .addCase(fetchAccountsAutopayStatus.fulfilled, (state, action) => {
        state.autopayLoading = false;
        state.autopayStatus = action.payload;
        state.autopayLastFetched = Date.now();
      })
      .addCase(fetchAccountsAutopayStatus.rejected, (state, action) => {
        state.autopayLoading = false;
        state.error = action.payload;
      });
  },
});

export default accountsSlice.reducer;
